import { cloneDeep } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';

import {
  dynamicAttributesFields,
  INITIAL_DYNAMIC_ATTRIBUTES,
} from 'hb-builder/components/BuilderComponents/dynamic/Publication/constants';
import type { PublicationItemAttributes } from 'hb-builder/components/BuilderComponents/dynamic/Publication/types';
import ManualItemsSelector from 'hb-builder/components/common/SettingsLibrary/CommonSettings/ManualItemsSelector';
import SelectSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/SelectSetting';
import TypeOfFillSelectSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/TypeOfFillSelectSetting';
import AccordionTitle from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/AccordionTitle';
import { useBuilderConfigContext } from 'hb-builder/hooks';
import type { ComponentSettingsTab } from 'hb-builder/types/settings';
import type {
  Categories,
  Option,
} from 'hb-builder/utils/dynamicSelectorsHelpers';
import {
  dataTypeSelectorCategories,
  dataTypeSelectorOptions,
  getContentTypesOptions,
  sanitizeQueryBuild,
} from 'hb-builder/utils/dynamicSelectorsHelpers';
import { useGetDynamicCategories } from 'hb-react/backoffice/requests/dynamicComponents/useGetDynamicCategories';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';

import FieldsDrawer from './FieldsDrawer';

const Content = ({
  item,
  onChange,
}: Pick<
  ComponentSettingsTab<PublicationItemAttributes>,
  'item' | 'onChange'
>) => {
  const {
    hivebrite: {
      ids: { topic },
      scope,
    },
    features: {
      dynamic: {
        publication: {
          [scope]: { withLatestPostsOption, withTopPostsOption },
        },
      },
    },
  } = useBuilderConfigContext();
  const flags = useFeatureFlag();
  const { flagBuilderGroupsContentInGroups } = flags;
  const isInTopicContext = Boolean(topic);

  const showTopics: boolean = isInTopicContext
    ? flagBuilderGroupsContentInGroups
    : true;

  const [categories, setCategories] = useState<Partial<Categories>>({});

  const { query, type } = item.attributes?.dynamicAttributes || {};

  const contentTypeIsSet = type !== 'empty';
  const settingProps = { item, onChange };

  const { data, isLoading } = useGetDynamicCategories({
    type: type || 'empty',
    enabled: query?.dataType !== 'manually',
  });

  useEffect(() => {
    if (type && query) {
      sanitizeQueryBuild({
        contentType: type,
        id: item.id,
        onChange,
        queryOptions: query,
      });

      if (contentTypeIsSet && data) {
        setCategories((prevState) => ({
          ...prevState,
          [scope]: {
            [type]: {
              categories: data.map(({ name }) => ({
                value: name,
                label: name,
              })),
            },
          },
        }));
      }
    }
  }, [contentTypeIsSet, data, item.id, scope, onChange, query, type]);

  const dataTypesCategories: Option[] = useMemo(
    () =>
      type && scope
        ? dataTypeSelectorCategories({
            categories: categories as Categories,
            networkLevel: scope,
            type,
          })
        : [],
    [categories, scope, type],
  );

  const dataTypesOptions = useMemo(
    () =>
      dataTypeSelectorOptions({
        includeManualSearch: true,
        includePastEvents: flags.flagBuilderPastEventsAdvancedComponents,
        networkLevel: scope,
        type,
      }),
    [flags, scope, type],
  );

  const handleChangeAndReset = ({ id, value }) => {
    onChange({
      id,
      settingKey: 'attributes.dynamicAttributes',
      value: {
        ...cloneDeep({
          ...INITIAL_DYNAMIC_ATTRIBUTES,
          fields: dynamicAttributesFields[value],
        }),
        type: value,
      },
    });
  };

  const handleChangeDataType = ({ id, settingKey, value }) => {
    onChange({
      id,
      values: [
        {
          settingKey,
          value,
        },
        {
          settingKey: 'attributes.dynamicAttributes.itemId',
          value: null,
        },
      ],
    });
  };

  return (
    <Wrapper>
      <AccordionTitle isInitiallyOpen title={__('Type of content')}>
        <SelectSetting
          item={item}
          onChange={handleChangeAndReset}
          options={getContentTypesOptions({
            showPosts: withLatestPostsOption || withTopPostsOption,
            showTopics,
          })}
          settingKey="dynamicAttributes.type"
          title={s__('Builder|Choose a type of content')}
          disabled={false}
          isLoading={false}
        />
      </AccordionTitle>
      {contentTypeIsSet && (
        <>
          <AccordionTitle isInitiallyOpen title={__('Content settings')}>
            <TypeOfFillSelectSetting
              disabled={dataTypesOptions.length === 1}
              item={item}
              onChange={handleChangeDataType}
              options={dataTypesOptions}
            />
            {query?.dataType === 'manually' ? (
              <ManualItemsSelector
                idSettingKey="attributes.dynamicAttributes.itemId"
                itemSettingKey="attributes.dynamicAttributes._selectedItem"
                type={type}
                {...settingProps}
              />
            ) : (
              <SelectSetting
                disabled={dataTypesCategories.length === 0 || isLoading}
                isLoading={isLoading}
                isMulti
                key={type}
                options={dataTypesCategories}
                placeholder={s__('Builder|Choose some categories')}
                settingKey="dynamicAttributes.query.categories"
                title={s__('Builder|Filter by categories')}
                {...settingProps}
              />
            )}
          </AccordionTitle>
          <FieldsDrawer contentType={type} {...settingProps} />
        </>
      )}
    </Wrapper>
  );
};

export default Content;

const Wrapper = styled(Box).attrs(() => ({
  flexDirection: 'column',
  width: '100%',
}))``;
